// import lib(s)
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Typography from '@material-ui/core/Typography';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
// Replacement for CustomDatePickerComponent
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
  KeyboardTimePicker,
} from '@material-ui/pickers';

// import custom components
import EcgLoader from 'legacy/containers/EcgLoader';
import CustomSingleSelectComboBox from '../../components/Common/FormMUIWrapperComponents/CustomSingleSelectComboBox';

// import constants
import { getTimeZones } from '../../../legacy/constants/constants';
import { SELECT_FIELD_CONST } from '../../components/Common/FormMUIWrapperComponents/CustomSingleSelect/Constants';

// import styles
import styles from './NotificationReminders.css';

const NotificationReminders = props => {
  const { data, handleNotificationRemainderSubmit, me } = props;
  const dispatch = useDispatch();
  const [startDateTime, setStartDateTime] = useState(new Date());
  const [endDateTime, setEndDateTime] = useState(new Date());
  const [selectedTimeZone, setSelectedTimeZone] = useState({});
  const [reminderStatus, setReminderStatus] = useState('off');
  const [formErrorState, setFormErrorState] = useState(true);
  const [TIME_ZONES, setTIME_ZONES] = useState(getTimeZones());
  const [updatedData, setUpdatedData] = useState({});

  // data is updated on successful POST / PUT
  useEffect(() => {
    let DATA = {};
    if (data && data.data && data.data[0]) {
      DATA = data.data[0]; //eslint-disable-line
    } else if (
      data &&
      data.data &&
      Object.prototype.hasOwnProperty.call(data.data, 'id')
    ) {
      DATA = data.data;
    }
    setUpdatedData(DATA);
  }, [data]);

  // based on data update from API process the data fo UI representation
  useEffect(() => {
    const today = new Date(); // Initially GET data will be blank [], new Date() is assigned
    const {
      startTime = today,
      endTime = today,
      reminderEnabled = false,
      timezone = '',
    } = updatedData;
    const updatedTimeZone = TIME_ZONES.find(t => t.value === timezone);

    // initially its a date object, check if string (passed from backend)
    typeof startTime === 'string'
      ? setStartDateTime(startTime.replace('Z', ''))
      : setStartDateTime(startTime);

    // initially its a date object, check if string (passed from backend)
    typeof endTime === 'string'
      ? setEndDateTime(endTime.replace('Z', ''))
      : setEndDateTime(endTime);

    // update timezone
    setSelectedTimeZone(updatedTimeZone);
    // update reminder state
    setReminderStatus(reminderEnabled ? 'on' : 'off');
  }, [updatedData]);

  // Event on change of timeZone selection
  const handleTimeZoneChange = (e, stateKey, val) => {
    setSelectedTimeZone(val);
  };

  const handleRemainderEnableChange = e => {
    setReminderStatus(e.target.value);
  };

  // validate Form Error state based on data selection
  useEffect(() => {
    // check if all the component's form state is valid, set the main form error state to false
    if (
      startDateTime !== undefined &&
      startDateTime !== '' &&
      endDateTime !== undefined &&
      endDateTime !== '' &&
      selectedTimeZone !== undefined &&
      selectedTimeZone !== null &&
      selectedTimeZone.hasOwnProperty('value') // eslint-disable-line no-prototype-builtins
    ) {
      setFormErrorState(false);
    } else {
      setFormErrorState(true);
    }
  }, [startDateTime, endDateTime, selectedTimeZone]);

  // on submit set data, perform action
  const handleSubmit = () => {
    // if id is present, it is updated data from API GET/POST/PUT
    const method =
      updatedData &&
      Object.prototype.hasOwnProperty.call(updatedData, 'id') &&
      updatedData.id !== null
        ? 'PUT'
        : 'POST';
    const { teams } = (me && me.data) || {};
    const notificationTemplatesConfig =
      (teams &&
        teams[0].notificationTemplates &&
        teams[0].notificationTemplates[0]) ||
      {};
    const {
      id: notificationTemplateId,
      rRuleTemplate,
      createdAt,
      updatedAt,
    } = notificationTemplatesConfig;
    const payload = {
      notificationTemplateId,
      rRule: rRuleTemplate,
      createdAt,
      updatedAt,
      moreData: {},
      reminderEnabled: reminderStatus.toLowerCase() === 'on',
      timezone: selectedTimeZone.value,
      startTime: `${
        new Date(startDateTime).toISOString().split('T')[0]
      }T${new Date(startDateTime).toLocaleTimeString('en-US', {
        hour12: false,
      })}Z`,
      endTime: `${new Date(endDateTime).toISOString().split('T')[0]}T23:59:59Z`,
      ...(method === 'PUT' && { id: updatedData.id }),
    };
    dispatch(handleNotificationRemainderSubmit(payload, method));
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.contentHeader}>
        <aside className={styles.title}>Reminders</aside>
        <aside className={styles.warningText}>All Fields Required</aside>
      </div>
      <p className={styles.helperInfoText}>
        Please use the fields below to activate reminders for this patient. Once
        complete click the set reminder button to initiate reminders.
      </p>
      <div className={styles.reminderForm}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={styles.formLabel}
            >
              Reminder Status
            </Typography>
            <div>
              <RadioGroup
                row
                aria-label="position"
                name="position"
                defaultValue="top"
                value={reminderStatus}
                onChange={handleRemainderEnableChange}
              >
                <FormControlLabel
                  value="on"
                  control={<Radio color="primary" />}
                  label="ON"
                />
                <FormControlLabel
                  value="off"
                  control={<Radio color="primary" />}
                  label="OFF"
                />
              </RadioGroup>
            </div>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={styles.formLabel}
            >
              Start Date
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              className={styles.formLabelGuideText}
            >
              Set day to send reminder
            </Typography>
            <div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  // disableFuture
                  // disabled={isEdit}
                  style={{ marginTop: '8px', background: 'white' }}
                  openTo="year"
                  variant="inline"
                  size="small"
                  inputVariant="outlined"
                  format="MM-dd-yyyy"
                  margin="normal"
                  placeholder="MM-DD-YYYY"
                  id="startDate"
                  views={['year', 'month', 'date']}
                  minDate={new Date().setDate(new Date().getDate() - 90)}
                  value={startDateTime}
                  onChange={setStartDateTime}
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </div>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={styles.formLabel}
            >
              Start Time
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              className={styles.formLabelGuideText}
            >
              Set time to send reminder
            </Typography>
            <div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardTimePicker
                  autoOk
                  // disabled={isEdit}
                  style={{ marginTop: '8px', background: 'white' }}
                  variant="inline"
                  size="small"
                  inputVariant="outlined"
                  margin="normal"
                  id="startTime"
                  value={startDateTime}
                  onChange={setStartDateTime}
                  inputVariant="outlined"
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </div>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={styles.formLabel}
            >
              End Date
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              className={styles.formLabelGuideText}
            >
              Set day to end reminders
            </Typography>
            <div>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  autoOk
                  // disableFuture
                  // disabled={isEdit}
                  style={{ marginTop: '8px', background: 'white' }}
                  openTo="year"
                  variant="inline"
                  size="small"
                  inputVariant="outlined"
                  format="MM-dd-yyyy"
                  margin="normal"
                  placeholder="MM-DD-YYYY"
                  id="endDate"
                  views={['year', 'month', 'date']}
                  minDate={new Date().setDate(new Date().getDate() + 1)}
                  value={endDateTime}
                  onChange={setEndDateTime}
                  inputVariant="outlined"
                  KeyboardButtonProps={{
                    'aria-label': 'change date',
                  }}
                  fullWidth
                />
              </MuiPickersUtilsProvider>
            </div>
          </Grid>
          <Grid item xs={6}>
            <Typography
              variant="subtitle2"
              gutterBottom
              className={styles.formLabel}
            >
              Time Zone
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              className={styles.formLabelGuideText}
            >
              Set patients time zone for recieving reminders
            </Typography>
            <div style={{ marginTop: '12px', background: 'white' }}>
              <CustomSingleSelectComboBox
                // label="Policy Holder Relationship"
                placeholder="Select time zone"
                variant={SELECT_FIELD_CONST.VARIANT.OUTLINED}
                menuItems={TIME_ZONES}
                valueKey="value"
                labelKeys={['label']}
                mandatory={false}
                selectedValue={selectedTimeZone}
                stateKey="selectedTimeZone"
                // validateForm={validateForm}
                handleChange={handleTimeZoneChange}
              />
            </div>
          </Grid>
        </Grid>
      </div>
      <div className={styles.submitButtonDiv}>
        <button
          type="button"
          className={
            formErrorState ? styles.PrimaryButtonDisabled : styles.PrimaryButton
          }
          onClick={handleSubmit}
          disabled={formErrorState}
        >
          SET REMINDER
        </button>
      </div>
    </div>
  );
};

export default NotificationReminders;



// WEBPACK FOOTER //
// ./src/views/containers/NotificationReminders/NotificationReminders.jsx